import { API_BASE_URL, ApiError } from './client';

export interface UnitMembership {
  unitId: string;
  unitName: string;
  unitType: string;
  /** 'None' for a guardian, who holds no membership in this unit. */
  permissionLevel: 'None' | 'Member' | 'Officer' | 'Admin';
  positionTitle: string;
  /**
   * The unit's IANA zone. Needed before an event exists: the new-event form has to say which zone
   * the times it is collecting will be read in.
   */
  timeZoneId: string;
  /**
   * The unit's own word for the group its members are organised into — "Patrol"/"Patrols" in a
   * Troop, "Den"/"Dens" in a Pack. Every screen renders these rather than a literal, which is what
   * makes supporting a Pack a configuration change rather than a second set of screens.
   */
  subgroupLabelSingular: string;
  subgroupLabelPlural: string;
  /**
   * Whether this unit's youth take part in conversations — true in a Troop, false in a Pack,
   * where Cub Scouts are roster entities and never participants. Read by the forms that create a
   * whole-unit thread so they can say what that thread will be, rather than offering an audience
   * the unit cannot hold. Tested instead of `unitType === 'Pack'` deliberately: the unit type is
   * what decides this, once, on the server.
   */
  allowsYouthParticipants: boolean;
}

export interface Me {
  personId: string;
  displayName: string;
  memberships: UnitMembership[];
  isSystemAdmin?: boolean;
  /**
   * Signed in but not yet permitted — a young person waiting on a guardian's
   * account. Returned rather than refused so the app can say why, instead of
   * bouncing them to a sign-in screen they have already used.
   */
  accessBlockedReason?: string | null;
  /** True when this person is a parent or guardian of someone. Reveals /family. */
  isGuardian?: boolean;
}

export interface SignInMethods {
  google: boolean;
  magicLink: boolean;
  googleClientId: string | null;
}

/**
 * Every call sends the session cookie. `credentials: 'include'` is required even
 * though app and API are same-site in production, because they are different
 * origins and the browser will not attach cookies otherwise.
 */
async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let detail: string | undefined;
    try {
      const problem = await response.json();
      // Two shapes, because the API genuinely emits two. Endpoints answer with ProblemDetails
      // (`detail`/`title`); the rate limiter answers with `{ message }`, which is the shape
      // `files.ts` reads and which this function used to drop on the floor. A family turned away
      // by the sign-in limiter was shown "Request failed (429)." while the server had composed a
      // sentence saying what to do about it (TROOP-222).
      detail = problem.detail ?? problem.title ?? problem.message;
    } catch {
      // Non-JSON error body; the status carries enough.
    }
    throw new ApiError(response.status, detail ?? `Request failed (${response.status}).`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const authApi = {
  methods: () => call<SignInMethods>('/auth/methods'),

  /** Null rather than throwing on 401 — "signed out" is an ordinary state, not an error. */
  me: async (): Promise<Me | null> => {
    try {
      return await call<Me>('/auth/me');
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },

  signInWithGoogle: (credential: string) =>
    call<{ signedIn: boolean }>('/auth/google/token', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    }),

  /**
   * Asks for a sign-in code by email. Always resolves for any well-formed address — the server
   * answers 202 whether or not it matched a roster, so that this is not an oracle for "is this
   * family in this troop", and the screen must not undo that by reporting anything more specific
   * than the server did.
   */
  requestLink: (email: string, next?: string | null) =>
    call<{ message: string }>('/auth/request-link', {
      method: 'POST',
      body: JSON.stringify({ email, next: next ?? null }),
    }),

  /**
   * Exchanges the address and the code that arrived at it for a session.
   *
   * **The address goes back up with the code, and that is not redundancy.** Six digits collide, so
   * the server finds a row by address and then compares the code against it — a verify by code
   * alone would eventually match another family's row. See `SignInService.VerifyAsync`.
   *
   * **This throws where `requestLink` never does.** Asking for a code must not reveal whether an
   * address is on a roster; presenting a wrong one is the caller's own failure and telling them so
   * is the only way the screen can offer a retry. The server still says only *that code did not
   * work* — never which of expired, used, mistyped or unknown it was.
   */
  verifyCode: (email: string, code: string) =>
    call<{ redirectPath: string }>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  /** Founding invite: creates the account, because the invitee is on no roster yet. */
  acceptInvite: (credential: string, inviteToken: string) =>
    call<{ signedIn: boolean; inviteId: string }>('/auth/google/invite', {
      method: 'POST',
      body: JSON.stringify({ credential, inviteToken }),
    }),

  foundUnit: (input: {
    inviteId: string;
    unitType: string;
    unitNumber: string;
    name: string;
    timeZoneId: string;
  }) => call<{ unitId: string }>('/auth/found-unit', { method: 'POST', body: JSON.stringify(input) }),

  /** Binds the caller's own Google account to a roster entry. Never a sign-in as someone else. */
  claimAccount: (credential: string, claimToken: string) =>
    call<{ signedIn: boolean }>('/auth/google/claim', {
      method: 'POST',
      body: JSON.stringify({ credential, claimToken }),
    }),

  /**
   * The email halves of the two doors above (TROOP-224). Same claim, with a code proving the
   * address instead of Google proving it.
   *
   * These exist because the population the emailed code was built for — a family whose address is
   * not a Google one — could sign in and still get through neither door that creates an account.
   * What makes them safe is that both invitations are now bound to the address they were sent to,
   * so proving the mailbox is proving you are the invitee.
   */
  acceptInviteByEmail: (email: string, code: string, inviteToken: string) =>
    call<{ signedIn: boolean; inviteId: string }>('/auth/email/invite', {
      method: 'POST',
      body: JSON.stringify({ email, code, inviteToken }),
    }),

  claimAccountByEmail: (email: string, code: string, claimToken: string) =>
    call<{ signedIn: boolean }>('/auth/email/claim', {
      method: 'POST',
      body: JSON.stringify({ email, code, claimToken }),
    }),

  signOut: () => call<void>('/auth/sign-out', { method: 'POST' }),
};

export interface FoundingInvite {
  id: string;
  url: string;
  expiresAt: string;
  /** Never null since TROOP-224: an invite is bound to the address it was sent to. */
  restrictedToEmail: string;
}

export interface InviteSummary {
  id: string;
  /**
   * Empty on a *listed* invite where the response above cannot be, and that is history rather than
   * an oversight: rows issued before TROOP-224 carry no address, and they are exactly the ones the
   * migration revoked. The screen reads `revoked` for those, never this.
   */
  restrictedToEmail: string | null;
  note: string | null;
  issuedAt: string;
  expiresAt: string;
  claimedAt: string | null;
  resultingUnitName: string | null;
  revoked: boolean;
}

export interface AdminUnitRow {
  id: string;
  name: string;
  unitType: string;
  memberCount: number;
}

export const adminApi = {
  /**
   * The address is required, not optional (TROOP-224). It was `string | null` and the null case
   * minted an invite anybody holding the URL could found a unit with; the server now refuses one,
   * so a caller passing null would get a 400 rather than the old behaviour.
   */
  createInvite: (restrictedToEmail: string, note: string | null) =>
    call<FoundingInvite>('/admin/invites', {
      method: 'POST',
      body: JSON.stringify({ restrictedToEmail, note }),
    }),

  listInvites: () => call<InviteSummary[]>('/admin/invites'),

  revokeInvite: (id: string) => call<void>(`/admin/invites/${id}/revoke`, { method: 'POST' }),

  listUnits: () => call<AdminUnitRow[]>('/admin/units'),
};

export interface RosterRow {
  personId: string;
  displayName: string;
  classification: 'Youth' | 'Adult' | 'Unknown';
  /** 'None' for a guardian, who holds no membership in this unit. */
  permissionLevel: 'None' | 'Member' | 'Officer' | 'Admin';
  positionTitle: string;
  /**
   * Null for a young person unless you are one of their guardians. Leaders and
   * unit admins never receive it -- an address here would be a way to contact a
   * Scout off-system, with no second adult and no record.
   */
  email: string | null;
  /**
   * A contact number, and **always null for a young person** — the product holds none for them at
   * all (TROOP-132). Rule 10's reasoning applied to a more immediate channel: a leader who can
   * text a Scout directly has one adult, no second adult and no record. Their parents' numbers are
   * how a leader reaches them.
   */
  phone: string | null;
  hasAccount: boolean;
  guardianNames: string[];
  /**
   * Whether this screen may offer a sign-up link. Always false for a young
   * person — their parent issues it from the family page, never a leader.
   */
  canClaimAccount: boolean;
  /** A parent of someone on this roster who holds no membership of their own. */
  isGuardianOnly: boolean;
}

export const rosterApi = {
  list: (unitId: string) => call<RosterRow[]>(`/units/${unitId}/roster/`),

  addAdult: (
    unitId: string,
    input: {
      givenName: string;
      familyName: string;
      email: string | null;
      permissionLevel: string;
      positionTitle: string;
      phone?: string | null;
    },
  ) => call<{ personId: string }>(`/units/${unitId}/roster/adults`, {
    method: 'POST',
    body: JSON.stringify(input),
  }),

  addYouth: (
    unitId: string,
    input: {
      givenName: string;
      familyName: string;
      dateOfBirth: string | null;
      guardians: { givenName: string; familyName: string; email: string | null }[];
      positionTitle: string;
    },
  ) => call<{ personId: string }>(`/units/${unitId}/roster/youth`, {
    method: 'POST',
    body: JSON.stringify(input),
  }),

  /**
   * Sets or clears an adult's contact number. Blank clears it, so correcting a typo needs no
   * second control.
   *
   * The server refuses this for a young person with a sentence saying why — a leader will
   * otherwise read it as a bug, because they can see the Scout on the very same screen.
   */
  setPhone: (unitId: string, personId: string, phone: string | null) =>
    call<void>(`/units/${unitId}/roster/${personId}/phone`, {
      method: 'PUT',
      body: JSON.stringify({ phone }),
    }),

  /**
   * An adult's address (TROOP-224). Adults only — the server refuses a young person with a
   * sentence naming their guardian, because rule 10 is about a leader never having it.
   *
   * **No null, unlike `setPhone`.** A number is contact detail; an address is what a sign-in
   * resolves to, so clearing one would lock that adult out of their own account.
   */
  setEmail: (unitId: string, personId: string, email: string) =>
    call<{ cancelledSignUpLink: boolean }>(`/units/${unitId}/roster/${personId}/email`, {
      method: 'PUT',
      body: JSON.stringify({ email }),
    }),

  claimLink: (unitId: string, personId: string) =>
    call<{ personId: string; displayName: string; url: string; expiresAt: string }>(
      `/units/${unitId}/roster/${personId}/claim-link`,
      { method: 'POST' },
    ),
};

export interface Child {
  personId: string;
  displayName: string;
  /** Visible to a parent and to nobody else, and set here rather than by a leader. */
  email: string | null;
  hasAccount: boolean;
  unitNames: string[];
}

/**
 * A parent's own area. Not under /units/ — guardians hold no membership, so a
 * unit-scoped route would refuse the very people these calls are for.
 */
export const familyApi = {
  children: () => call<Child[]>('/family/'),

  /**
   * `cancelledSignUpLink` says whether this withdrew the sign-up link already sent to the old
   * address (TROOP-232). The server's answer, like `copySentTo` below: "did the address change" is
   * `PersonEmail.Normalize`'s question, and a card comparing raw strings calls a case-only edit a
   * change and hides a link that is still live.
   */
  setEmail: (youthId: string, email: string | null) =>
    call<{ email: string | null; cancelledSignUpLink: boolean }>(`/family/${youthId}/email`, {
      method: 'PUT',
      body: JSON.stringify({ email }),
    }),

  /**
   * `copySentTo` is the address the parent's own copy went to, or null when they have none on
   * file (TROOP-223). The server's answer because the client cannot derive it — the card must not
   * claim a copy was sent when no message was composed.
   */
  claimLink: (youthId: string) =>
    call<{
      personId: string;
      displayName: string;
      url: string;
      expiresAt: string;
      copySentTo: string | null;
    }>(`/family/${youthId}/claim-link`, { method: 'POST' }),
};
